import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { displaySettingsAPI } from '../services/api';
import { resolveDisplay } from '../config/displayRegistry';

/**
 * useDisplay — the customer-screen display customization hook.
 *
 * Fetches the effective overrides for the current user's company once,
 * caches them in React Query, and returns { visible, label } accessors. If
 * the fetch is still in flight or has errored, the accessors fall back to
 * the registry defaults so the page renders correctly on first paint (no
 * flicker, no blank labels).
 *
 * Visibility overrides apply to CUSTOMER views only — admin views (any
 * /admin/ route, or a customer route opened with ?from=admin) always show
 * every field so staff never lose sight of data. Label renames apply
 * everywhere, keeping terminology consistent across both sides. An admin
 * can preview the true customer view by dropping ?from=admin from the URL.
 *
 * Usage:
 *   const d = useDisplay();
 *   d.visible('agreementDetail.details.company')  // boolean
 *   d.label('agreementDetail.details.company')    // string
 */
export function useDisplay() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdminView =
    location.pathname.startsWith('/admin/') || searchParams.get('from') === 'admin';

  const { data } = useQuery({
    queryKey: ['displaySettings'],
    queryFn: () => displaySettingsAPI.getEffective().then(res => res.data.settings || {}),
    retry: 1,
    // If the endpoint is unavailable, quietly fall back to defaults; the page
    // still renders because resolveDisplay handles the missing-overrides case.
    onError: () => {},
  });

  return useMemo(() => {
    const overrides = data || {};
    return {
      visible: (key) => (isAdminView ? true : resolveDisplay(key, overrides).visible),
      label: (key) => resolveDisplay(key, overrides).label,
      resolve: (key) => resolveDisplay(key, overrides),
    };
  }, [data, isAdminView]);
}
